import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Space, Modal, message, Tag, DatePicker, Select, Row, Col, Card } from 'antd';
import { SearchOutlined, FileTextOutlined, DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { getConsultantBillingGroups, previewConsultantBilling, generateConsultantBillingPDF } from '../../services/loanService';
import { formatCurrency } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Page from '../../components/layout/Page';
import { Link, useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ConsultantBillingAdminOverview = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [previewModalVisible, setPreviewModalVisible] = useState(false);
    const [selectedBillingNumber, setSelectedBillingNumber] = useState(null);
    const [previewContent, setPreviewContent] = useState(null);
    const [consultants, setConsultants] = useState([]);
    const [selectedConsultant, setSelectedConsultant] = useState(null);
    const [dateRange, setDateRange] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        filterData();
    }, [data, selectedConsultant, dateRange]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getConsultantBillingGroups();
            setData(response.data);
            
            // Extract unique consultants for filter
            const uniqueConsultants = [...new Map(response.data.map(item => [
                item.consultant.id, 
                { id: item.consultant.id, name: item.consultant.full_name }
            ])).values()];
            setConsultants(uniqueConsultants);
        } catch (error) {
            message.error('Failed to fetch billing data');
            console.error('Error fetching billing data:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const filterData = () => {
        let filtered = [...data];
        
        // Filter by consultant
        if (selectedConsultant) {
            filtered = filtered.filter(item => item.consultant.id === selectedConsultant);
        }
        
        // Filter by date range
        if (dateRange && dateRange[0] && dateRange[1]) {
            const startDate = dateRange[0].startOf('day');
            const endDate = dateRange[1].endOf('day');
            
            filtered = filtered.filter(item => {
                const itemDate = dayjs(item.billing_date);
                return itemDate.isAfter(startDate) && itemDate.isBefore(endDate);
            });
        }
        
        setFilteredData(filtered);
    };
    
    const handleConsultantChange = (value) => {
        setSelectedConsultant(value);
    };
    
    const handleDateRangeChange = (dates) => {
        setDateRange(dates);
    };
    
    const resetFilters = () => {
        setSelectedConsultant(null);
        setDateRange(null);
    };


    const handlePreview = async (billingNumber) => {
        try {
            setSelectedBillingNumber(billingNumber);
            setPreviewModalVisible(true);
            const response = await previewConsultantBilling(billingNumber);
            setPreviewContent(response.data);
        } catch (error) {
            message.error('Failed to load preview');
            console.error('Error loading preview:', error);
        }
    };

    const handleGeneratePDF = async (billingNumber) => {
        try {
            await generateConsultantBillingPDF(billingNumber);
            message.success('PDF generated successfully');
        } catch (error) {
            message.error('Failed to generate PDF');
            console.error('Error generating PDF:', error);
        }
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
    });

    const expandedRowRender = (record) => {
        const columns = [
            { title: 'Transaktionsnummer', dataIndex: 'process_number', key: 'process_number' },
            { title: 'Kunde', dataIndex: ['customer', 'name'], key: 'customer_name' },
            { title: 'Bank', dataIndex: ['bank', 'name'], key: 'bank' },
            { 
                title: 'Betrag', 
                dataIndex: 'amount', 
                key: 'amount',
                render: (amount) => formatCurrency(amount),
                align: 'right'

            },              
            { 
                title: 'Bonus', 
                dataIndex: 'extra_bonus', 
                key: 'extra_bonus',
                render: (extra_bonus) => formatCurrency(extra_bonus),
                align: 'right'

            },            
            { 
                title: 'Provision', 
                dataIndex: 'primary_consultant_share', 
                key: 'primary_consultant_share',
                render: (primary_consultant_share) => formatCurrency(primary_consultant_share),
                align: 'right'

            }
        ];

        return (
            <Table
                columns={columns}
                dataSource={record.loans}
                pagination={false}
                rowKey="id"
            />
        );
    };

    const columns = [
        {
            title: 'Rechnungsnummer',
            dataIndex: 'billing_number',
            key: 'billing_number',
            ...getColumnSearchProps('billing_number'),
            render: (text, record) => (
                <span>
                    {record.billing_number} - {record.consultant?.full_name || 'N/A'}
                </span>
            ),
        },
        {
            title: 'Datum',
            dataIndex: 'billing_date',
            key: 'billing_date',
            render: (date) => date ? dayjs(date).format('DD.MM.YYYY') : '-',
            sorter: (a, b) => new Date(a.billing_date) - new Date(b.billing_date),
            align: 'right'

        },
        {
            title: 'Bonus',
            dataIndex: 'total_extra_bonus',
            key: 'total_extra_bonus',
            render: (total_extra_bonus) => formatCurrency(total_extra_bonus),
            sorter: (a, b) => a.total_extra_bonus - b.total_extra_bonus,
            align: 'right'

        },        
        {
            title: 'Provision',
            dataIndex: 'total_primary_consultant_share',
            key: 'total_primary_consultant_share',
            render: (total_primary_consultant_share) => formatCurrency(total_primary_consultant_share),
            sorter: (a, b) => a.total_primary_consultant_share - b.total_primary_consultant_share,
            align: 'right'

        },
        {
            title: 'Gesamtbetrag',
            dataIndex: 'total_sum',
            key: 'total_sum',
            render: (total_sum) => formatCurrency(total_sum),
            sorter: (a, b) => a.total_sum - b.total_sum,
            align: 'right'

        }
    ];


    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={24} sm={24} md={12} lg={8}>
                <Button type="primary" onClick={() => navigate('/accountant/consultant-billing-admin')}>
                    {t('consultantBilling.addInvoice') || 'Neue Abrechnung'}
                </Button>
            </Col>
        </Row>
    );

    return (
        <Page 
            title={t('consultantBilling.title')}
            subtitle={t('consultantBilling.subtitle')}
            headerExtra={headerExtra}
        >
        <div>
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col span={8}>
                        <label style={{ marginRight: 8 }}>Berater:</label>
                        <Select
                            style={{ width: '80%' }}
                            placeholder="Wähle einen Berater"
                            onChange={handleConsultantChange}
                            value={selectedConsultant}
                            allowClear
                        >
                            {consultants.map(consultant => (
                                <Option key={consultant.id} value={consultant.id}>
                                    {consultant.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={10}>
                        <label style={{ marginRight: 8 }}>Zeitraum:</label>
                        <RangePicker
                            onChange={handleDateRangeChange}
                            value={dateRange}
                            format="DD.MM.YYYY"
                        />
                    </Col>
                    <Col span={6}>
                        <Button 
                            icon={<ReloadOutlined />} 
                            onClick={resetFilters}
                            style={{ marginRight: 8 }}
                        >
                            Filter zurücksetzen
                        </Button>
                    </Col>
                </Row>
            </Card>
            
            <Table
                columns={columns}
                dataSource={filteredData.length > 0 ? filteredData : data}
                loading={loading}
                rowKey="billing_number"
                expandable={{
                    expandedRowRender,
                    rowExpandable: record => record.loans?.length > 0,
                }}
            />

            <Modal
                title="Invoice Preview"
                visible={previewModalVisible}
                onCancel={() => setPreviewModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setPreviewModalVisible(false)}>
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleGeneratePDF(selectedBillingNumber)}
                    >
                        Download PDF
                    </Button>
                ]}
                width={800}
            >
                {previewContent && (
                    <div dangerouslySetInnerHTML={{ __html: previewContent }} />
                )}
            </Modal>
        </div>
        </Page>
    );
};

export default ConsultantBillingAdminOverview;